"use client";

import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import type { SignInModalView } from "@/lib/auth/sign-in-modal-store";
import { AccountDetailsForm } from "./account-details-form";
import { ConfirmEmailStep } from "./steps/confirm-email-step";
import { EmailCodeStep } from "./steps/email-code-step";
import { LogInStep } from "./steps/log-in-step";
import { SignUpStep } from "./steps/sign-up-step";

export type AuthStep = SignInModalView;

export interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryPoint: AuthEntryPoint;
  initialStep?: AuthStep;
  reason?: string;
  notice?: string;
  initialEmail?: string;
  initialCode?: string;
  quoteClaimToken?: string;
  showProviders?: boolean;
  collectDetails?: boolean;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
}

const TITLES: Record<AuthStep, string> = {
  "log-in": "Log in",
  "sign-up": "Create your account",
  code: "Log in with an emailed code",
  verify: "Check your email",
  finish: "Finish signing up",
};

function useAuthSteps(props: AuthDialogProps) {
  const [step, setStep] = useState<AuthStep>(props.initialStep ?? "log-in");
  const [email, setEmail] = useState(props.initialEmail ?? "");
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const onSignedIn = async (result: SignedIn) => {
    if (props.collectDetails && result.isNew) {
      setSignedIn(result);
      setStep("finish");
      return;
    }
    await props.onSignedIn(result);
  };
  const finishDetails = () => {
    if (signedIn) void props.onSignedIn(signedIn);
    else void props.onSignedIn({ status: "signed_in", isNew: true });
  };
  const switchTo = (next: AuthStep) => (currentEmail: string) => {
    setEmail(currentEmail);
    setStep(next);
  };
  return { step, setStep, email, onSignedIn, finishDetails, switchTo };
}

function StepBody(props: AuthDialogProps & { steps: ReturnType<typeof useAuthSteps> }) {
  const { steps } = props;
  const shared = { entryPoint: props.entryPoint, onSignedIn: steps.onSignedIn };
  const toVerify = () => steps.setStep("verify");
  const toLogIn = () => steps.setStep("log-in");
  const showProviders = props.showProviders === true;
  const bodies: Record<AuthStep, () => ReactNode> = {
    "log-in": () => (
      <LogInStep {...shared} initialEmail={steps.email} quoteClaimToken={props.quoteClaimToken} notice={props.notice} showProviders={showProviders}
        onNeedsCode={toVerify} onUseCode={steps.switchTo("code")} onSignUp={steps.switchTo("sign-up")} />
    ),
    "sign-up": () => (
      <SignUpStep {...shared} initialEmail={steps.email} quoteClaimToken={props.quoteClaimToken} showProviders={showProviders}
        onNeedsCode={toVerify} onLogIn={steps.switchTo("log-in")} />
    ),
    code: () => (
      <EmailCodeStep {...shared} initialEmail={steps.email} initialCode={props.initialCode} quoteClaimToken={props.quoteClaimToken} onNeedsCode={toVerify} onBack={steps.switchTo("log-in")} />
    ),
    verify: () => <ConfirmEmailStep {...shared} onStartOver={toLogIn} />,
    finish: () => <AccountDetailsForm onDone={steps.finishDetails} onSignedOut={toLogIn} />,
  };
  return bodies[steps.step]();
}

export function AuthDialog(props: AuthDialogProps) {
  const steps = useAuthSteps(props);
  const description = steps.step === "log-in" ? props.reason : undefined;
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{TITLES[steps.step]}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <StepBody {...props} steps={steps} />
      </DialogContent>
    </Dialog>
  );
}
